// ═══════════════════════════════════════════════════════════════════════════
// LES DEUX PRIX ANNONCÉS PUBLIQUEMENT — la définition, et rien d'autre
// ───────────────────────────────────────────────────────────────────────────
// POURQUOI CE FICHIER EXISTE À CÔTÉ DE `catalogue-services.js`, ALORS QUE
// GATE B DIT « tout prix vient du catalogue ». Il en vient toujours : le
// catalogue IMPORTE ces deux constantes et les pose sur ses services `paie` et
// `onboarding`. Il n'y a donc qu'UNE définition, ici.
//
// La raison de la séparation est mesurée, pas esthétique : `catalogue-services.js`
// pèse 82 Ko et ne peut pas être élagué (`getPrixService` lit `ALL_SERVICES`,
// donc le tableau entier est retenu). Or les deux prix publics sont cités dans
// des pages PUBLIQUES qui sont des composants client — `app/pricing/page.js` et
// `app/vitrine/page.js` portent toutes deux `'use client'`. Les brancher sur le
// catalogue aurait expédié 82 Ko de définitions de services au navigateur de
// chaque prospect, sur les deux pages qui doivent charger le plus vite.
// (Le tableau de bord, lui, embarquait déjà le catalogue dans 7 écrans : là,
// ça ne change rien. C'est le PUBLIC qui paie la différence.)
//
// ⚠️ CE FICHIER NE DOIT JAMAIS RIEN IMPORTER. C'est sa seule raison d'être :
// il est minuscule et sans dépendance, donc gratuit à embarquer partout. Un
// import ajouté ici annulerait l'économie sans que personne ne le voie.
//
// 📏 Mesure du 01/09/2026 : ces deux nombres étaient recopiés à la main dans
// **253 endroits sur 53 fichiers** — blog, vitrine, pages tarifs, landings,
// JSON-LD, mails de prospection, image OG, scripts de vente, prompts d'agents.
// Le jour où le fondateur passe la fiche de 29 à 32 €, il ne doit pas exister
// 253 endroits à retrouver : c'est le défaut #2604, où la fiche dirigeant
// valait 19 €/an d'un côté et 228 € de l'autre, et c'est la version chère qui
// partait dans le PDF prospect.
// ═══════════════════════════════════════════════════════════════════════════

/** 29 € HTVA — le prix d'appel, par fiche de paie. */
export const PRIX_FICHE = 29;

/** 149 € — frais d'affiliation & implémentation, une seule fois. */
export const PRIX_IMPLEMENTATION = 149;

// ═══════════════════════════════════════════════════════════════════════════
// SUBSTITUTION DANS UN TEXTE TRADUIT — `auPrix()`
// ───────────────────────────────────────────────────────────────────────────
// POURQUOI CE HELPER EXISTE, et c'est un défaut LATENT trouvé le 01/09/2026.
//
// `tx()` (lang-context) est une recherche par CHAÎNE FRANÇAISE EXACTE : elle
// prend la phrase française et va chercher son entrée dans le dictionnaire.
// Injecter le prix directement dans une phrase qui part ensuite dans `tx()`
// marche donc… tant que le prix ne change pas. Le jour où il passe à 32 €, la
// phrase ne correspond plus à aucune clé, `tx()` rend le FRANÇAIS, et un
// visiteur néerlandophone lit une phrase française sans que rien ne casse.
//
// C'est exactement ce qui était en place dans `GuideDemoCommerciale.js`, rendu
// par `{tx(step.tip)}` : vert aujourd'hui, faux à la première indexation.
//
// LA SOLUTION : la clé garde un ESPACE RÉSERVÉ (`%prixFiche%`), présent à
// l'identique dans les quatre langues, et la substitution se fait APRÈS la
// recherche. La traduction survit au changement de prix.
//
//   auPrix(tx('Tarifs — %prixFiche%€/fiche'))
//
// ⚠️ POURQUOI DES `%` ET PAS DES ACCOLADES : `{prixFiche}` est ambigu avec la
// syntaxe du langage — `${prixFiche}` est une interpolation, `value={prixFiche}`
// un attribut JSX, et les deux existent vraiment dans `FacturationFiches.js`.
// Le garde-fou accusait donc un écran correct. `%prixFiche%` n'est la syntaxe
// de rien : le verrou devient exact au lieu d'être bardé d'exceptions.
//
// ⚠️ Un `%prixFiche%` qui arriverait à l'écran sans passer par `auPrix()`
// s'afficherait tel quel. `__tests__/prix-placeholder-substitue.test.js` le
// vérifie sur tout `app/`.
// ═══════════════════════════════════════════════════════════════════════════
export function auPrix(texte) {
  return String(texte ?? '')
    .replace(/%prixFiche%/g, PRIX_FICHE)
    .replace(/%prixImplementation%/g, PRIX_IMPLEMENTATION);
}
